import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Table, Card, Tag, Image, Tooltip, Typography, Select, DatePicker, Button, Space, message } from 'antd';
import { getAllImages } from '../../graphql/queries';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRowLinkProps, withRowLink } from '../../utils/rowLink';
import './Images.css';

const { Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const availableModels = [
  { value: 'FAL', label: 'FAL' },
  { value: 'FAL_VEO3_FAST', label: 'FAL_VEO3_FAST' },
  { value: 'FAL_OVI', label: 'FAL_OVI' },
  { value: 'WIRO_PX', label: 'WIRO_PX' },
  { value: 'SERVICE', label: 'SERVICE' },
];

const Images = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState({
    current: parseInt(searchParams.get('page')) || 1,
    pageSize: parseInt(searchParams.get('pageSize')) || 10,
  });
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const { loading, data, refetch } = useQuery(getAllImages, {
    variables: {
      page: pagination.current,
      pageSize: pagination.pageSize,
      status: selectedStatus,
      model: selectedModel,
      startDate: dateRange && dateRange[0] ? dateRange[0].toISOString() : null,
      endDate: dateRange && dateRange[1] ? dateRange[1].toISOString() : null,
      type: selectedType && selectedType !== 'all' ? selectedType : null
    },
  });

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', pagination.current.toString());
    params.set('pageSize', pagination.pageSize.toString());
    if (selectedStatus) params.set('status', selectedStatus);
    if (selectedModel) params.set('model', selectedModel);
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.set('startDate', dateRange[0].toISOString());
      params.set('endDate', dateRange[1].toISOString());
    }
    if (selectedType) params.set('type', selectedType);
    setSearchParams(params);
  }, [pagination, selectedStatus, selectedModel, dateRange, setSearchParams]);

  const getStatusColor = (status) => {
    const colors = {
      SUCCEEDED: 'green',
      FAILED: 'red',
      RUNNING: 'blue',
      IN_QUE: 'orange',
      PENDING: 'purple',
      THROTTLED: 'gold',
      MODERATION: 'magenta'
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'url',
      key: 'url',
      width: 90,
      render: (url) => (
        url ? (
          <Image
            src={url}
            alt="Image"
            style={{ width: '70px', height: '70px', objectFit: 'cover' }}
            preview={{
              maskClassName: 'customize-mask',
              mask: <div>View</div>
            }}
          />
        ) : '-'
      ),
    },
    {
      title: 'Prompt',
      dataIndex: 'prompt',
      key: 'prompt',
      width: 180,
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          <Paragraph ellipsis={{ rows: 1 }} style={{ marginBottom: 0, maxWidth: 160 }}>
            {text}
          </Paragraph>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const displayText = {
          SUCCEEDED: 'Succeeded',
          FAILED: 'Failed',
          RUNNING: 'Running',
          IN_QUE: 'In Queue',
          PENDING: 'Pending',
          THROTTLED: 'Throttled',
          MODERATION: 'Moderation'
        };
        return (
          <Tag color={getStatusColor(status)}>
            {displayText[status] || status}
          </Tag>
        );
      },
      filterDropdown: () => (
        <Select
          style={{ width: 120, margin: 8 }}
          value={selectedStatus}
          onChange={(value) => {
            setSelectedStatus(value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
          placeholder="Filter status"
        >
          <Option value="RUNNING">Running</Option>
          <Option value="FAILED">Failed</Option>
          <Option value="SUCCEEDED">Succeeded</Option>
          <Option value="IN_QUE">In Queue</Option>
          <Option value="PENDING">Pending</Option>
          <Option value="THROTTLED">Throttled</Option>
          <Option value="MODERATION">Moderation</Option>
        </Select>
      ),
      filtered: !!selectedStatus,
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 150,
      render: (model) => model,
      filterDropdown: () => (
        <Select
          style={{ width: 150, margin: 8 }}
          value={selectedModel}
          onChange={(value) => {
            setSelectedModel(value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
          placeholder="Filter model"
        >
          {availableModels.map(model => (
            <Option key={model.value} value={model.value}>
              {model.value}
            </Option>
          ))}
        </Select>
      ),
      filtered: !!selectedModel,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      filterDropdown: () => (
        <Select
          style={{ width: 150, margin: 8 }}
          value={selectedType}
          onChange={(value) => {
            setSelectedType(value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
          placeholder="Filter type"
        >
          <Option value="text2Image">text2Image</Option>
          <Option value="img2Image">img2Image</Option>
        </Select>
      ),
      filtered: !!selectedType && selectedType !== 'all',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
  ];

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleRowClick = (record) => {
    navigate(`/image/${record.id}`);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const exportToCSV = async () => {
    try {
      message.loading('Fetching all data for export...', 0);

      const result = await refetch({
        page: 1,
        pageSize: 10000,
        status: selectedStatus,
        model: selectedModel,
        startDate: dateRange && dateRange[0] ? dateRange[0].toISOString() : null,
        endDate: dateRange && dateRange[1] ? dateRange[1].toISOString() : null,
      });

      message.destroy();

      if (!result.data?.getAllImages?.images || result.data.getAllImages.images.length === 0) {
        message.warning('No data to export');
        return;
      }

      const imagesToExport = result.data.getAllImages.images;

      message.success(`Exporting ${imagesToExport.length} images to CSV...`);

      const csvContent = [
        ['Prompt', 'URL', 'Created At'],
        ...imagesToExport.map(image => [
          image.prompt || '',
          image.url || '',
          new Date(image.createdAt).toLocaleString('tr-TR')
        ])
      ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `images_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Successfully exported ${imagesToExport.length} images!`);
    } catch (error) {
      message.destroy();
      message.error('Failed to export data: ' + error.message);
    }
  };

  return (
    <div className="images-container">
      <Card title="Images">
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            placeholder={['Start Date', 'End Date']}
            style={{ width: 300 }}
            showTime={false}
            format="YYYY-MM-DD"
          />
          <Button
            type="primary"
            onClick={exportToCSV}
            size="small"
            style={{ height: '32px' }}
            disabled={!dateRange || !dateRange[0] || !dateRange[1] || !data?.getAllImages?.images || data.getAllImages.images.length === 0}
          >
            Export CSV
          </Button>
        </div>
        <Table
          columns={withRowLink(columns, (record) => `/image/${record.id}`, { exclude: ['url'] })}
          dataSource={data?.getAllImages?.images || []}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            total: data?.getAllImages?.totalCount,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} images`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            ...getRowLinkProps(`/image/${record.id}`, () => handleRowClick(record)),
          })}
        />
      </Card>
    </div>
  );
};

export default Images;
